import { isNative } from './platform';

export type MapProvider = 'google' | 'apple' | 'waze';

interface MapLinkParams {
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  name?: string;
}

export function getMapUrl(provider: MapProvider, params: MapLinkParams): string {
  const hasCoords = params.latitude != null && params.longitude != null;
  const query = params.name
    ? `${params.name}${params.address ? ', ' + params.address : ''}`
    : params.address ?? '';
  const encoded = encodeURIComponent(query);

  switch (provider) {
    case 'google':
      return hasCoords
        ? `https://www.google.com/maps/search/?api=1&query=${params.latitude},${params.longitude}`
        : `https://www.google.com/maps/search/?api=1&query=${encoded}`;
    case 'apple':
      return hasCoords
        ? `https://maps.apple.com/?ll=${params.latitude},${params.longitude}&q=${encodeURIComponent(params.name ?? query)}`
        : `https://maps.apple.com/?q=${encoded}`;
    case 'waze':
      return hasCoords
        ? `https://waze.com/ul?ll=${params.latitude},${params.longitude}&navigate=yes`
        : `https://waze.com/ul?q=${encoded}&navigate=yes`;
  }
}

export const MAP_PROVIDERS: { id: MapProvider; label: string }[] = [
  { id: 'google', label: 'Google Maps' },
  { id: 'apple', label: 'Apple Maps' },
  { id: 'waze', label: 'Waze' },
];

/**
 * Opens a location in maps.
 * - Browser: opens Google Maps directly in a new tab.
 * - Native app: returns the list of providers so the caller can show a picker.
 */
export function openInMaps(params: MapLinkParams): 'native' | void {
  if (isNative) return 'native';
  window.open(getMapUrl('google', params), '_blank', 'noopener');
}

// ── Geocoding (Nominatim / OpenStreetMap) ─────────────────────────────────────

export interface GeocodingResult {
  lat: number;
  lng: number;
  displayName: string;
}

export async function geocodeAddress(query: string): Promise<GeocodingResult[]> {
  if (!query.trim()) return [];
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    headers: { 'Accept-Language': 'en' },
  });
  if (!res.ok) return [];
  const data = await res.json() as Array<{ lat: string; lon: string; display_name: string }>;
  return data.map((r) => ({
    lat: parseFloat(r.lat),
    lng: parseFloat(r.lon),
    displayName: r.display_name,
  }));
}
